import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { InvoicesService } from '../../invoices/invoices.service';
import { InvoiceTypeOption, UpsertVendorInput, Vendor } from '../../invoices/invoices.types';
import { VendorTypeService } from '../vendor-type/vendor-type.service';
import { VendorType } from '../vendor-type/vendor-type.types';

interface VendorFormState {
  name: string;
  vendorTypeId: number | null;
  description: string;
  defaultCurrency: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  stateProvince: string;
  postalCode: string;
  countryCode: string;
  phoneNumber: string;
  emittedInvoiceConceptIds: number[];
}

const EMPTY_VENDOR_FORM: VendorFormState = {
  name: '',
  vendorTypeId: null,
  description: '',
  defaultCurrency: 'USD',
  addressLine1: '',
  addressLine2: '',
  city: '',
  stateProvince: '',
  postalCode: '',
  countryCode: '',
  phoneNumber: '',
  emittedInvoiceConceptIds: [],
};

@Component({
  selector: 'app-vendor-catalog',
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './vendor.html',
  styleUrls: ['../vendor-profile.css', './vendor.css'],
})
export class VendorCatalogComponent implements OnInit {
  private readonly pageSize = 10;

  protected readonly vendors = signal<Vendor[]>([]);
  protected readonly searchTerm = signal('');
  protected readonly pageNumber = signal(1);
  protected readonly invoiceTypeOptions = signal<InvoiceTypeOption[]>([]);
  protected readonly vendorTypeOptions = signal<VendorType[]>([]);
  protected readonly isEditorOpen = signal(false);
  protected readonly attachmentName = signal<string | null>(null);
  protected readonly attachmentFile = signal<File | null>(null);
  protected readonly validationMessage = signal('');
  protected readonly isSaving = signal(false);
  protected readonly loadError = signal('');
  protected readonly form = signal<VendorFormState>({ ...EMPTY_VENDOR_FORM });

  protected readonly filteredVendors = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) {
      return this.vendors();
    }

    return this.vendors().filter((vendor) => {
      return [
        vendor.name,
        vendor.vendorTypeName,
        vendor.description,
        vendor.defaultCurrency,
        vendor.phoneNumber,
        this.formatAddress(vendor),
        this.formatInvoiceTypes(vendor.emittedInvoiceTypes),
      ].some((field) => (field ?? '').toLowerCase().includes(term));
    });
  });

  protected readonly totalPages = computed(() => Math.max(1, Math.ceil(this.filteredVendors().length / this.pageSize)));

  protected readonly pagedVendors = computed(() => {
    const start = (this.pageNumber() - 1) * this.pageSize;
    return this.filteredVendors().slice(start, start + this.pageSize);
  });

  protected readonly rangeLabel = computed(() => {
    const total = this.filteredVendors().length;
    if (total === 0) {
      return 'No records';
    }

    const start = (this.pageNumber() - 1) * this.pageSize + 1;
    const end = Math.min(this.pageNumber() * this.pageSize, total);
    return `Showing ${start} to ${end} of ${total} vendors`;
  });

  constructor(
    private readonly invoicesService: InvoicesService,
    private readonly vendorTypeService: VendorTypeService,
  ) {}

  ngOnInit(): void {
    forkJoin({
      invoiceTypeOptions: this.invoicesService.getInvoiceTypeOptions(),
      vendorTypeOptions: this.vendorTypeService.getActiveVendorTypeOptions(),
      vendors: this.invoicesService.getVendors(),
    }).subscribe({
      next: ({ invoiceTypeOptions, vendorTypeOptions, vendors }) => {
        this.invoiceTypeOptions.set(invoiceTypeOptions);
        this.vendorTypeOptions.set(vendorTypeOptions);
        this.vendors.set(vendors);
      },
      error: () => {
        this.loadError.set('Unable to load vendor data from the API.');
      },
    });
  }

  protected onSearchChange(value: string): void {
    this.searchTerm.set(value);
    this.pageNumber.set(1);
  }

  protected goToPreviousPage(): void {
    if (this.pageNumber() > 1) {
      this.pageNumber.set(this.pageNumber() - 1);
    }
  }

  protected goToNextPage(): void {
    if (this.pageNumber() < this.totalPages()) {
      this.pageNumber.set(this.pageNumber() + 1);
    }
  }

  protected openCreateVendor(): void {
    this.validationMessage.set('');
    this.form.set({
      ...EMPTY_VENDOR_FORM,
      emittedInvoiceConceptIds: this.invoiceTypeOptions()[0] ? [this.invoiceTypeOptions()[0].id] : [],
    });
    this.attachmentFile.set(null);
    this.attachmentName.set(null);
    this.isEditorOpen.set(true);
  }

  protected closeEditor(): void {
    this.isEditorOpen.set(false);
    this.validationMessage.set('');
  }

  protected onFieldChange<K extends keyof VendorFormState>(key: K, value: VendorFormState[K]): void {
    this.form.set({ ...this.form(), [key]: value });
  }

  protected toggleInvoiceType(typeId: number, selected: boolean): void {
    const current = this.form().emittedInvoiceConceptIds;
    if (selected && !current.includes(typeId)) {
      this.onFieldChange('emittedInvoiceConceptIds', [...current, typeId]);
      return;
    }

    if (!selected && current.includes(typeId)) {
      this.onFieldChange('emittedInvoiceConceptIds', current.filter((item) => item !== typeId));
    }
  }

  protected hasInvoiceType(typeId: number): boolean {
    return this.form().emittedInvoiceConceptIds.includes(typeId);
  }

  protected onAttachmentSelected(files: FileList | null): void {
    const selected = files?.item(0) ?? null;
    this.attachmentFile.set(selected);
    this.attachmentName.set(selected?.name ?? null);
  }

  protected clearAttachment(): void {
    this.attachmentFile.set(null);
    this.attachmentName.set(null);
  }

  protected saveVendor(): void {
    if (this.isSaving()) {
      return;
    }

    this.validationMessage.set('');
    const payload = this.buildPayload();
    if (!payload) {
      return;
    }

    this.isSaving.set(true);
    this.invoicesService.createVendor(payload).subscribe({
      next: ({ result: createdId, documentError }) => {
        this.isSaving.set(false);
        if (createdId <= 0) {
          this.validationMessage.set('Unable to create vendor.');
          return;
        }

        this.reloadVendors();
        this.closeEditor();
        if (documentError) {
          window.KTToast?.show({
            message: `Vendor created, but the document could not be uploaded. ${documentError}`,
            variant: 'warning',
          });
          return;
        }

        window.KTToast?.show({ message: 'Vendor created successfully.', variant: 'success' });
      },
      error: () => {
        this.isSaving.set(false);
        this.validationMessage.set('Unable to create vendor.');
      },
    });
  }

  protected deleteVendor(vendor: Vendor): void {
    const confirmed = window.confirm(`Delete vendor "${vendor.name}"?`);
    if (!confirmed) {
      return;
    }

    this.invoicesService.deleteVendor(vendor.id).subscribe({
      next: (removed) => {
        if (!removed) {
          window.KTToast?.show({ message: 'Unable to delete vendor.', variant: 'destructive' });
          return;
        }

        this.reloadVendors();
        window.KTToast?.show({ message: 'Vendor deleted successfully.', variant: 'success' });
      },
      error: () => {
        window.KTToast?.show({ message: 'Unable to delete vendor.', variant: 'destructive' });
      },
    });
  }

  protected formatInvoiceTypes(types: string[]): string {
    return types.join(', ');
  }

  protected formatAddress(vendor: Vendor): string {
    const cityLine = [vendor.city, vendor.stateProvince, vendor.postalCode]
      .map((part) => (part ?? '').trim())
      .filter((part) => !!part)
      .join(', ');

    const parts = [vendor.addressLine1, vendor.addressLine2, cityLine, vendor.countryCode]
      .map((part) => (part ?? '').trim())
      .filter((part) => !!part);

    return parts.length > 0 ? parts.join(' · ') : '-';
  }

  private reloadVendors(): void {
    this.invoicesService.getVendors().subscribe({
      next: (vendors) => {
        this.vendors.set(vendors);
        const cappedPage = Math.min(this.pageNumber(), Math.max(1, this.totalPages()));
        this.pageNumber.set(cappedPage);
      },
      error: () => {
        this.loadError.set('Unable to reload vendor data from the API.');
      },
    });
  }

  private buildPayload(): UpsertVendorInput | null {
    const currentForm = this.form();
    if (!currentForm.name.trim() || !currentForm.vendorTypeId || !currentForm.defaultCurrency.trim()) {
      this.validationMessage.set('Vendor name, vendor type, and default currency are required.');
      return null;
    }

    if (currentForm.emittedInvoiceConceptIds.length === 0) {
      this.validationMessage.set('Select at least one billing concept.');
      return null;
    }

    return {
      name: currentForm.name.trim(),
      vendorTypeId: currentForm.vendorTypeId,
      description: currentForm.description.trim(),
      defaultCurrency: currentForm.defaultCurrency.trim().toUpperCase(),
      addressLine1: currentForm.addressLine1.trim(),
      addressLine2: currentForm.addressLine2.trim(),
      city: currentForm.city.trim(),
      stateProvince: currentForm.stateProvince.trim(),
      postalCode: currentForm.postalCode.trim(),
      countryCode: currentForm.countryCode.trim().toUpperCase(),
      phoneNumber: currentForm.phoneNumber.trim(),
      emittedInvoiceConceptIds: [...currentForm.emittedInvoiceConceptIds],
      newDocument: this.attachmentFile() ? {
        file: this.attachmentFile() as File,
        fileName: this.attachmentName() || (this.attachmentFile() as File).name,
        description: 'Vendor document',
        type: 'SUPPORT',
      } : null,
    };
  }
}
